import React, {useContext, useEffect, useState} from 'react'
import {useNavigate} from "react-router-dom";
import "./landing.css"
import {Context} from "../store/Store";
import Actions from "../store/Actions";
import {Whoami, GetPublicPlans, SandboxLogin} from "../api/Api";
import Loading from "./Loading";
import {PARENT_COMPANY, ParentMark} from "../lib/brand";
import {COMPARISON_ROWS, formatBytes, isFairUse} from "../lib/planComparison";
import usePageMeta from "../hooks/usePageMeta";
import useSandboxStatus from "../components/Sandbox/useSandboxStatus";
import {sectionPath, DEFAULT_SECTION} from "../components/Project/sectionPaths";
import backlogShot from "./img/shots/backlog.png";
import kanbanShot from "./img/shots/kanban.png";
import designShot from "./img/shots/design.png";
import ganttShot from "./img/shots/gantt.png";

// Marketing copy for the feature sections, in the order they're read. Each
// one names the screenshot slot it sits next to (see SHOTS below).
const FEATURES = [
    {
        id: "planning",
        eyebrow: "Sprint planning & bug tracking",
        title: "The all-in-one for small teams and game studios",
        body: "Trackadillo puts sprint planning and bug tracking in the same place, with scope management to keep the project under control as it grows. It's built for small teams and game developers.",
        bullets: [
            "Velocity weighted toward your most recent sprints, not a flat average",
            "One public link takes bug reports from testers and clients, no account needed",
            "Scope management keeps every phase of the project under control",
            "Drag straight from the backlog and watch the point budget fill",
        ],
        shot: "backlog",
        caption: "Backlog planning with the predicted point budget",
    },
    {
        id: "design",
        eyebrow: "Design & scope",
        title: "The spec lives next to the work it describes",
        body: "Write the design page for a feature in the app, and link tasks and bugs straight into the prose. No more hunting for the doc that explains why a ticket exists.",
        bullets: [
            "A design page per scope, organised by the scope types you define",
            "Inline links to tasks and bugs that open right there on the page",
            "Phases and scopes that feed the Gantt without a second plan to maintain",
        ],
        shot: "design",
        caption: "A design page with linked tasks",
    },
    {
        id: "board",
        eyebrow: "Board & Gantt",
        title: "Kanban for the day-to-day, Gantt for the long view",
        body: "The board updates live for everyone looking at it, so status is never a guessing game. Zoom out to the Gantt to see phases and scopes lined up against the calendar — the view for when someone asks what lands in March.",
        bullets: [
            "Drag tasks across columns and swimlanes, live for every viewer",
            "Rename, reorder and add Kanban columns to match how you actually work",
            "Gantt view across phases and scopes for the longer horizon",
            "Per-person contribution on every sprint",
        ],
        shot: "gantt",
        caption: "Phases and scopes lined up against the calendar",
    },
];

// Screenshots aren't in the repo yet, so every slot renders a labelled
// placeholder at the right shape. To fill one in: drop the image in
// src/pages/img/shots/, import it at the top of this file, and set it here.
// Nothing else changes - the frame, caption and layout are already in place.
const SHOTS = {
    hero: kanbanShot,
    backlog: backlogShot,
    design: designShot,
    gantt: ganttShot,
};

const Screenshot = ({name, caption, wide}) => (
    <figure className={wide ? "shot wide" : "shot"}>
        {SHOTS[name]
            ? <img src={SHOTS[name]} alt={caption}/>
            : <div className={"shotPlaceholder"}>
                <span className={"shotPlaceholderLabel"}>Screenshot</span>
                <span className={"shotPlaceholderFile"}>src/pages/img/shots/{name}.png</span>
            </div>}
        {caption && <figcaption>{caption}</figcaption>}
    </figure>
);

// Lines that aren't limits - the part of a tier's pitch that can't be read
// off a Plan row. Everything else on a pricing card comes from the API.
const TIER_EXTRAS = {
    STARTER: ["Email support", "No card, no expiry"],
    PLUS: ["Organization & role management", "Email support"],
    PRO: ["Everything in Plus", "Support ticket management", "API access & integrations", "Expedited support"],
};

// Trackadillo is a PXLPLZ product, and that's said out loud in three places
// on this page - the nav, the pricing section and the footer. The reason is
// as practical as it is legal: a renewal shows up on a card statement as
// PXLPLZ, and a charge from a name the customer doesn't recognise is how
// chargebacks start. The name, wording and logo live in lib/brand.js.

const FAQS = [
    {
        q: "What happens when the 30-day trial ends?",
        a: "You pick a plan and pay, or you don't. Nothing is deleted and nobody is charged automatically — an account that doesn't convert simply drops back to Starter, and you'd trim down to the free limits or upgrade to carry on where you left off.",
    },
    {
        q: "Do I need a credit card to try Plus or Pro?",
        a: "No. The trial starts the moment you sign up, with no payment method on file. We ask for one at the end of the 30 days, not the beginning.",
    },
    {
        q: "Is Starter really free?",
        a: "Yes — free forever, up to 5 people and 3 projects. It isn't a trial that lapses, and we don't ask for a card to start one.",
    },
    {
        q: "Can I switch plans or cancel later?",
        a: "Any time, from your billing settings. Cancelling keeps your plan running until the end of the period you've already paid for, then drops the account to Starter.",
    },
    {
        q: "What counts toward the people limit?",
        a: "Everyone with access to the project, whether they were added directly or came in through an organization. On Starter you'd remove someone to add a sixth.",
    },
];

const formatPrice = (cents) => {
    if (cents === 0) return "Free";
    return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
};

// Monthly equivalent of an annual price, for the "works out at" line under
// the yearly figure.
const monthlyEquivalent = (cents) => `$${(cents / 100 / 12).toFixed(2)}`;

const Check = ({on}) => (
    on
        ? <span className={"cell yes"} aria-label={"Included"}>✓</span>
        : <span className={"cell no"} aria-label={"Not included"}>—</span>
);

// A comparison row's value is either a boolean (an included/not-included
// mark) or something to print as-is - see lib/planComparison.
const comparisonCell = (value) => (typeof value === "boolean" ? <Check on={value}/> : value);

// The card bullets, built from the same limits the API enforces so a change
// to a tier can't leave the pricing page quietly lying about it.
const cardHighlights = (plan) => {
    const l = plan.limits;
    return [
        isFairUse(plan, "maxProjects") ? "Unlimited projects*" : `${l.maxProjects} project${l.maxProjects === 1 ? "" : "s"}`,
        `${l.maxUsersPerProject} people per project`,
        l.allowAnonymousBugEntry ? "Anonymous bug reporting" : "Bug tracking for your team",
        `${formatBytes(l.maxImageUploadBytes)} uploads at ${l.imageMaxWidth} × ${l.imageMaxHeight}`,
        ...(TIER_EXTRAS[plan.tier] || []),
    ];
};

// Root ("/") entry point. Decides where to send the visitor:
//   - no accessToken and no user id in memory -> show this landing page
//   - either is present -> verify with the real whoami endpoint, then
//     forward to /dashboard on success or /login on failure
export default props => {
    const [state, setState] = useContext(Context);
    const navigate = useNavigate();

    usePageMeta(
        "Trackadillo — Sprint Planning & Bug Tracking for Small Teams",
        "Plan the sprint your team can finish. The board, the backlog, the Gantt, the design docs and the bug queue in one place.",
    );

    // Captured once on mount - by design we always navigate away rather than
    // ever flipping this back to false, so it never needs to be state.
    const hasSession = Boolean(state.user.accessToken) || Boolean(state.user.id);
    const checking = hasSession;

    const [plans, setPlans] = useState([]);
    const [plansFailed, setPlansFailed] = useState(false);
    // Named `billing` rather than `interval` so the state setter doesn't
    // shadow window.setInterval inside this component.
    const [billing, setBilling] = useState("MONTH");

    // Both declared here, before the `if (checking)` return below, not
    // further down near trySandbox where they'd read more naturally -
    // every hook has to run on every render regardless of branches taken
    // later in the function body, and trySandbox itself flips hasSession
    // true (via SET_USER) before navigate() unmounts this page, which
    // would otherwise make this component take the early return on its
    // very next render and skip them.
    const sandbox = useSandboxStatus();
    const [sandboxEntering, setSandboxEntering] = useState(false);

    useEffect(() => {
        if (!hasSession) {
            return;
        }

        Whoami(state.user.accessToken).then(({user}) => {
            setState({type: Actions.SET_USER, payload: {...user, accessToken: state.user.accessToken}});
            navigate("/dashboard", {replace: true});
        }).catch(() => {
            setState({type: Actions.SET_USER, payload: {id: null, name: null, accessToken: null}});
            navigate("/login", {replace: true});
        });
        // Only run this check once, when landing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pricing is the only part of this page that needs the API - the rest
    // renders immediately, and a failure here costs the visitor the pricing
    // grid rather than the whole page.
    useEffect(() => {
        if (hasSession) {
            return;
        }

        GetPublicPlans()
            .then(({plans}) => setPlans(plans))
            .catch(() => setPlansFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (checking) {
        return <Loading/>;
    }

    const startSignUp = (plan) => {
        if (!plan || plan.tier === "STARTER") {
            return navigate("/sign-up?plan=starter");
        }
        navigate(`/sign-up?plan=${plan.tier.toLowerCase()}&billing=${billing.toLowerCase()}`);
    };

    // Signs in as the shared sandbox account (see api/routes/auth.js's POST
    // /auth/sandbox) and drops straight into its project - no form, no
    // account. Only rendered at all once useSandboxStatus confirms one is
    // set up (see below), so a failure here is a genuine, surprising error
    // rather than the ordinary "not configured" case GetSandboxStatus
    // already swallows.
    const trySandbox = () => {
        setSandboxEntering(true);
        SandboxLogin().then(({accessToken, user, project}) => {
            setState({type: Actions.SET_USER, payload: {...user, accessToken}});
            navigate(sectionPath(project.publicId, DEFAULT_SECTION));
        }).catch(() => {
            setSandboxEntering(false);
            alert("Something went wrong opening the sandbox. Please try again.");
        });
    };

    const trialDays = plans.find((plan) => plan.trialDays > 0)?.trialDays || 30;
    // Only offer the billing toggle if something is actually sold annually.
    const hasAnnual = plans.some((plan) => plan.prices.YEAR);

    return (
        <div className={"landingPage"}>
            <header className={"landingNav"}>
                <div className={"landingNavInner"}>
                    <div className={"wordmarkBlock"}>
                        {/* The tag sits on the wordmark's own line rather than
                            under it, where the byline already is - so the name
                            reads as "Trackadillo (beta)" and the parent company
                            stays a separate statement. */}
                        <div className={"beta-tag-line"}>
                            <div className={"wordmark"}>Trackadillo</div>
                            <span className={"beta-tag"}>Beta</span>
                        </div>
                        <div className={"byline"}>by <ParentMark className={"parentMark"}/></div>
                    </div>
                    <nav className={"navLinks"}>
                        <a href={"#features"}>Features</a>
                        <a href={"#pricing"}>Pricing</a>
                        <a href={"#faq"}>FAQ</a>
                    </nav>
                    <div className={"navActions"}>
                        <button className={"ghostButton"} onClick={() => navigate("/login")}>Log in</button>
                        <button className={"primaryButton"} onClick={() => navigate("/sign-up")}>Start free</button>
                    </div>
                </div>
            </header>

            <section className={"hero"}>
                <div className={"heroFade"}></div>
                <div className={"heroInner"}>
                    <div className={"heroCopy"}>
                        <p className={"eyebrow"}>Sprint planning &amp; project tracking</p>
                        <h1>Let's get organized!</h1>
                        <p className={"heroBody"}>
                            The board, the backlog, the Gantt, the design docs and the bug queue in one
                            place; with a point budget worked out from the velocity your team has been
                            hitting.
                        </p>
                        <div className={"heroActions"}>
                            <button className={"primaryButton large"} onClick={() => navigate("/sign-up")}>
                                Start free
                            </button>
                            <button className={"ghostButton large"} onClick={() => navigate("/sign-up?plan=plus")}>
                                Try Plus for {trialDays} days
                            </button>
                        </div>
                        <p className={"heroFinePrint"}>
                            Free forever for up to 5 people. Paid plans include a {trialDays}-day
                            trial — no credit card.
                        </p>
                        {
                            sandbox.loaded && sandbox.available &&
                            <p className={"heroSandboxLink"}>
                                Or <a href={"#"} onClick={(e) => { e.preventDefault(); trySandbox(); }}>
                                    {sandboxEntering ? "opening the sandbox…" : "jump straight into a live sandbox"}
                                </a> — no sign-up.
                            </p>
                        }
                    </div>
                    <Screenshot name={"hero"} caption={"The sprint board with swimlanes"} wide/>
                </div>
            </section>

            <section className={"features"} id={"features"}>
                {FEATURES.map((feature, index) => (
                    <div className={index % 2 === 0 ? "feature" : "feature reversed"} key={feature.id} id={feature.id}>
                        <div className={"featureCopy"}>
                            <p className={"eyebrow"}>{feature.eyebrow}</p>
                            <h2>{feature.title}</h2>
                            <p>{feature.body}</p>
                            <ul>
                                {feature.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                            </ul>
                        </div>
                        <Screenshot name={feature.shot} caption={feature.caption}/>
                    </div>
                ))}
            </section>

            <section className={"pricing"} id={"pricing"}>
                <div className={"sectionHead"}>
                    <p className={"eyebrow"}>Pricing</p>
                    <h2>Start free. Pay when it's carrying real work.</h2>
                    <p className={"sectionLead"}>
                        Every paid plan starts with {trialDays} days free, with no payment method up front.
                    </p>
                </div>

                {hasAnnual && (
                    <div className={"intervalToggle"} role={"group"} aria-label={"Billing period"}>
                        <button
                            className={billing === "MONTH" ? "on" : ""}
                            onClick={() => setBilling("MONTH")}
                        >Monthly</button>
                        <button
                            className={billing === "YEAR" ? "on" : ""}
                            onClick={() => setBilling("YEAR")}
                        >Yearly <span className={"saveBadge"}>2 months free</span></button>
                    </div>
                )}

                {plansFailed && (
                    <p className={"plansError"}>
                        Pricing is having a moment — <a href={"/sign-up"}>start free</a> and we'll sort it out,
                        or reload the page.
                    </p>
                )}

                <div className={"planCards"}>
                    {plans.map((plan) => {
                        // A tier not sold on the selected interval (Starter has
                        // no annual price - it's free) falls back to monthly
                        // rather than disappearing from the comparison.
                        const price = plan.prices[billing] || plan.prices.MONTH;
                        const shownInterval = plan.prices[billing] ? billing : "MONTH";
                        const featured = plan.tier === "PLUS";

                        return (
                            <div className={featured ? "planCard featured" : "planCard"} key={plan.tier}>
                                {featured && <div className={"planFlag"}>Most teams start here</div>}
                                <h3>{plan.name}</h3>
                                <p className={"planTagline"}>{plan.tagline}</p>

                                <div className={"planPrice"}>
                                    <span className={"amount"}>{formatPrice(price.priceCents)}</span>
                                    {price.priceCents > 0 && (
                                        <span className={"per"}>/{shownInterval === "YEAR" ? "year" : "month"}</span>
                                    )}
                                </div>
                                {price.priceCents > 0 && shownInterval === "YEAR" && (
                                    <p className={"planSubPrice"}>works out at {monthlyEquivalent(price.priceCents)} a month</p>
                                )}
                                {plan.trialDays > 0 && (
                                    <p className={"planTrial"}>{plan.trialDays} days free, no card</p>
                                )}

                                <button
                                    className={featured ? "primaryButton full" : "ghostButton full"}
                                    onClick={() => startSignUp(plan)}
                                >
                                    {plan.trialDays > 0 ? `Start ${plan.name} trial` : "Get started free"}
                                </button>

                                <ul className={"planFeatures"}>
                                    {cardHighlights(plan).map((line) => <li key={line}>{line}</li>)}
                                </ul>
                            </div>
                        );
                    })}
                </div>

                {plans.length > 0 && (
                    <p className={"billingDisclosure"}>
                        Trackadillo is a product of <ParentMark className={"parentMark"}/>. Subscriptions are billed
                        through Stripe and appear on your statement as <strong>{PARENT_COMPANY}</strong> — worth
                        knowing before the first renewal lands.
                    </p>
                )}

                {plans.length > 0 && (
                    <div className={"comparisonWrap"}>
                        <table className={"comparison"}>
                            <thead>
                                <tr>
                                    <th scope={"col"}>Everything, side by side</th>
                                    {plans.map((plan) => <th scope={"col"} key={plan.tier}>{plan.name}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARISON_ROWS.map((row) => (
                                    <tr key={row.label}>
                                        <th scope={"row"}>{row.label}</th>
                                        {plans.map((plan) => (
                                            <td key={plan.tier}>{comparisonCell(row.render(plan.limits, plan))}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className={"footnote"}>
                            * Pro's ceilings are fair use rather than literally infinite — 100 projects and 100
                            custom types. If you're anywhere near that, talk to us and we'll sort it out.
                        </p>
                    </div>
                )}
            </section>

            <section className={"faq"} id={"faq"}>
                <div className={"sectionHead"}>
                    <p className={"eyebrow"}>Questions</p>
                    <h2>The things worth knowing before you sign up</h2>
                </div>
                <div className={"faqGrid"}>
                    {FAQS.map((item) => (
                        <div className={"faqItem"} key={item.q}>
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className={"closer"}>
                <h2>Put your next sprint in Trackadillo.</h2>
                <p>Free for up to 5 people, {trialDays} days of any paid plan, no card either way.</p>
                <div className={"closerActions"}>
                    <button className={"primaryButton large"} onClick={() => navigate("/sign-up")}>Start free</button>
                    <button className={"ghostButton large"} onClick={() => navigate("/login")}>Log in</button>
                </div>
            </section>

            <footer className={"landingFooter"}>
                <div className={"wordmarkBlock"}>
                    <div className={"wordmark"}>Trackadillo</div>
                    <div className={"byline"}>a <ParentMark className={"parentMark"}/> product</div>
                </div>
                <div className={"footerLinks"}>
                    <a href={"#features"}>Features</a>
                    <a href={"#pricing"}>Pricing</a>
                    <a href={"#faq"}>FAQ</a>
                    <a href={"/login"}>Log in</a>
                </div>
                <div className={"copyright"}>© {new Date().getFullYear()} {PARENT_COMPANY}</div>
            </footer>
        </div>
    )
}
